<script setup lang="ts">
import { EmptyState, formatDateTime } from '@yc/ui-kit'
import { computed } from 'vue'

import {
  EVENT_LABELS,
  EVENT_TAG,
  SIGN_RESULT_LABELS,
  SIGN_RESULT_TAG,
  STATUS_LABELS,
  STATUS_TAG,
  remainingQty,
  type SignResult,
  type TraceBundle,
  type TraceEventType,
} from '../api/shipments'
import {
  KIND_LABELS as RECEIPT_KIND_LABELS,
  STATUS_LABELS as RECEIPT_STATUS_LABELS,
  type ReceiptKind,
  type ReceiptStatus,
} from '../api/receipts'
import {
  KIND_LABELS as EXCEPTION_KIND_LABELS,
  LEVEL_LABELS,
  LEVEL_TAG,
  STATUS_LABELS as EXCEPTION_STATUS_LABELS,
  type ExceptionKind,
  type ExceptionLevel,
  type ExceptionStatus,
} from '../api/exceptions'

const props = defineProps<{ trace: TraceBundle }>()

/** 已签收件数：签收记录累计，与后端 listRow.signed_qty 的口径一致 */
const signedQty = computed(() => props.trace.pod_records.reduce((sum, r) => sum + r.signed_qty, 0))

/** 未关闭异常：open / handling 都还没关掉（后端 exception.ActiveStatuses） */
const activeExceptions = computed(
  () => props.trace.exceptions.filter((e) => e.status !== 'closed').length,
)

const eventLabel = (type: string): string => EVENT_LABELS[type as TraceEventType] ?? type
</script>

<template>
  <div class="yc-pod-trace">
    <div class="yc-pod-trace__facts">
      <div class="yc-pod-trace__fact">
        <span>状态</span>
        <el-tag :type="STATUS_TAG[trace.shipment.status]" size="small">
          {{ STATUS_LABELS[trace.shipment.status] ?? trace.shipment.status }}
        </el-tag>
      </div>
      <div class="yc-pod-trace__fact"><span>起运地</span>{{ trace.shipment.origin || '—' }}</div>
      <div class="yc-pod-trace__fact"><span>目的地</span>{{ trace.shipment.dest || '—' }}</div>
      <div class="yc-pod-trace__fact"><span>承运商</span>{{ trace.shipment.carrier || '—' }}</div>
      <div class="yc-pod-trace__fact"><span>行车任务号</span>{{ trace.shipment.trip_no || '—' }}</div>
      <div class="yc-pod-trace__fact"><span>订单号</span>{{ trace.shipment.order_no || '—' }}</div>
      <div class="yc-pod-trace__fact"><span>货描</span>{{ trace.shipment.cargo_desc || '—' }}</div>
      <div class="yc-pod-trace__fact">
        <span>件数</span>{{ signedQty }} / {{ trace.shipment.qty }}（剩余可签
        {{ remainingQty(trace.shipment, trace.pod_records) }}）
      </div>
      <div class="yc-pod-trace__fact"><span>重量</span>{{ Number(trace.shipment.weight_kg).toFixed(3) }} kg</div>
      <div class="yc-pod-trace__fact"><span>发运时间</span>{{ formatDateTime(trace.shipment.depart_at) }}</div>
      <div class="yc-pod-trace__fact"><span>到达时间</span>{{ formatDateTime(trace.shipment.arrive_at) }}</div>
      <div class="yc-pod-trace__fact"><span>签收时间</span>{{ formatDateTime(trace.shipment.signed_at) }}</div>
      <div class="yc-pod-trace__fact"><span>未关异常</span>{{ activeExceptions }}</div>
      <div class="yc-pod-trace__fact"><span>备注</span>{{ trace.shipment.remark || '—' }}</div>
    </div>

    <h3 class="yc-pod-trace__sect">追溯时间线（{{ trace.events.length }}）</h3>
    <el-timeline v-if="trace.events.length" class="yc-pod-trace__timeline">
      <el-timeline-item
        v-for="ev in trace.events"
        :key="ev.id"
        :type="EVENT_TAG[ev.event_type]"
        :timestamp="formatDateTime(ev.happened_at)"
        placement="top"
      >
        <div class="yc-pod-trace__ev">
          <b>{{ eventLabel(ev.event_type) }}</b>
          <span v-if="ev.location" class="yc-pod-trace__loc">{{ ev.location }}</span>
        </div>
        <p v-if="ev.detail" class="yc-pod-trace__detail">{{ ev.detail }}</p>
        <p class="yc-pod-trace__meta">操作人：{{ ev.operator || '—' }}</p>
      </el-timeline-item>
    </el-timeline>
    <EmptyState
      v-else
      title="暂无追溯事件"
      description="运单创建时会写入第一条「创建运单」事件，之后每个动作都会追加。"
    />

    <h3 class="yc-pod-trace__sect">签收记录（{{ trace.pod_records.length }}）</h3>
    <el-table v-if="trace.pod_records.length" :data="trace.pod_records" row-key="id" size="small">
      <el-table-column prop="signee" label="签收人" min-width="100" show-overflow-tooltip />
      <el-table-column prop="sign_phone" label="电话" min-width="120" show-overflow-tooltip />
      <el-table-column label="签收时间" min-width="150">
        <template #default="{ row }">{{ formatDateTime(row.sign_at) }}</template>
      </el-table-column>
      <el-table-column label="结果" width="100">
        <template #default="{ row }">
          <el-tag :type="SIGN_RESULT_TAG[row.result as SignResult]" size="small">
            {{ SIGN_RESULT_LABELS[row.result as SignResult] ?? row.result }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="件数" width="80" align="right">
        <template #default="{ row }">{{ row.signed_qty }}</template>
      </el-table-column>
      <el-table-column label="地点" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ row.location || '—' }}</template>
      </el-table-column>
      <el-table-column label="备注" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.remark || '—' }}</template>
      </el-table-column>
      <el-table-column prop="operator" label="操作人" min-width="90" show-overflow-tooltip />
    </el-table>
    <EmptyState
      v-else
      title="还没有签收记录"
      description="运单到达后在「签收」表单里录入，部分签收可多次录入。"
    />

    <h3 class="yc-pod-trace__sect">回单（{{ trace.receipts.length }}）</h3>
    <el-table v-if="trace.receipts.length" :data="trace.receipts" row-key="id" size="small">
      <el-table-column prop="receipt_no" label="回单号" min-width="150" />
      <el-table-column label="类型" width="100">
        <template #default="{ row }">
          <el-tag size="small" type="info">{{ RECEIPT_KIND_LABELS[row.kind as ReceiptKind] ?? row.kind }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag size="small">{{ RECEIPT_STATUS_LABELS[row.status as ReceiptStatus] ?? row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="提交时间" min-width="150">
        <template #default="{ row }">{{ formatDateTime(row.submitted_at) }}</template>
      </el-table-column>
      <el-table-column label="退回时间" min-width="150">
        <template #default="{ row }">{{ formatDateTime(row.returned_at) }}</template>
      </el-table-column>
      <el-table-column label="备注" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.remark || '—' }}</template>
      </el-table-column>
    </el-table>
    <EmptyState
      v-else
      title="还没有回单"
      description="运单签满后系统会自动生成一张电子回单，在回单台账里提交。"
    />

    <h3 class="yc-pod-trace__sect">签收异常（{{ trace.exceptions.length }}）</h3>
    <el-table v-if="trace.exceptions.length" :data="trace.exceptions" row-key="id" size="small">
      <el-table-column label="级别" width="90">
        <template #default="{ row }">
          <el-tag :type="LEVEL_TAG[row.level as ExceptionLevel]" size="small">
            {{ LEVEL_LABELS[row.level as ExceptionLevel] ?? row.level }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="90">
        <template #default="{ row }">{{ EXCEPTION_KIND_LABELS[row.kind as ExceptionKind] ?? row.kind }}</template>
      </el-table-column>
      <el-table-column prop="desc" label="描述" min-width="220" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag size="small">{{ EXCEPTION_STATUS_LABELS[row.status as ExceptionStatus] ?? row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="handled_by" label="处理人" min-width="90" show-overflow-tooltip>
        <template #default="{ row }">{{ row.handled_by || '—' }}</template>
      </el-table-column>
      <el-table-column label="处理时间" min-width="150">
        <template #default="{ row }">{{ formatDateTime(row.handled_at) }}</template>
      </el-table-column>
      <el-table-column label="备注" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.remark || '—' }}</template>
      </el-table-column>
    </el-table>
    <EmptyState
      v-else
      title="没有签收异常"
      description="拒收 / 破损签收会自动在这里生成一条待处理异常。"
    />
  </div>
</template>

<style scoped lang="scss">
.yc-pod-trace {
  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
    margin-bottom: var(--yc-space-4);
  }

  &__fact {
    font-size: var(--yc-font);

    span {
      margin-right: var(--yc-space-2);
      color: var(--yc-text-secondary);
      font-size: var(--yc-font-sm);
    }
  }

  &__sect {
    margin: var(--yc-space-5) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__timeline {
    padding-left: var(--yc-space-1);
  }

  &__ev {
    display: flex;
    align-items: baseline;
    gap: var(--yc-space-2);
    font-size: var(--yc-font);
  }

  &__loc {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__detail {
    margin: var(--yc-space-1) 0 0;
    color: var(--yc-text);
    font-size: var(--yc-font-sm);
    line-height: 1.7;
  }

  &__meta {
    margin: var(--yc-space-1) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }
}
</style>
